import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  EAddressType,
  EOrderStatus,
  type EReviewRejectionReason,
  EReviewStatusType,
  type IAddress,
  type IGetOrderResponse,
  type TOrderHistoryEntry,
} from '@mahawi/eshop-common/src/types';
import { createSlice } from '@reduxjs/toolkit';

import {
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
} from '../async-state';
import {
  type IFakturoidSyncContactResult,
  type IOrderCustomer,
  type IOrderInvoice,
  type IOrderState,
  type IVatIdVerificationResult,
} from './types';

export const preloadedState: IOrderState = {
  inProcess: false,
  order: undefined,
  invoices: [],
  loadedAt: 0,
  customer: undefined,
  fakturoidSyncInProcess: false,
  fakturoidSyncResult: null,
  vatIdVerificationInProcess: false,
  vatIdVerification: null,
  history: {
    orderUUID: null,
    entries: [],
    inProcess: false,
    noteInProcess: false,
    loadedAt: 0,
  },
};

const orderSlice = createSlice({
  name: 'Order',
  initialState: preloadedState,
  reducers: {
    orderLoad: {
      reducer(state) {
        state.inProcess = true;
        state.fakturoidSyncResult = null;
        state.vatIdVerificationInProcess = false;
        state.vatIdVerification = null;
      },
      prepare(orderUUID: string) {
        return { payload: { order: { uuid: orderUUID } } };
      },
    },
    orderLoadResponse: {
      reducer(
        state,
        action: {
          payload: {
            order: IGetOrderResponse | null;
            invoices: IOrderInvoice[];
          };
        },
      ) {
        state.inProcess = false;
        state.order = action.payload.order;
        state.invoices = action.payload.invoices;
        state.loadedAt = Date.now();
        state.order?.review?.products.sort((a, b) =>
          stringCompare(a.product.uuid, b.product.uuid),
        );
        state.order?.tags.sort((a: string, b: string): number =>
          stringCompare(a, b),
        );
      },
      prepare(order: IGetOrderResponse | null, invoices: IOrderInvoice[] = []) {
        return { payload: { order, invoices } };
      },
    },
    orderSetOrderStatusCode: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(orderUUID: string, statusCode: EOrderStatus) {
        return {
          payload: {
            order: { uuid: orderUUID, orderStatusCode: statusCode },
          },
        };
      },
    },
    reviewSetStatusCode: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(
        reviewUUID: string,
        statusCode: EReviewStatusType,
        rejectionReason?: EReviewRejectionReason,
      ) {
        return {
          payload: {
            review: {
              uuid: reviewUUID,
              reviewStatusCode: statusCode,
              rejectionReason,
            },
          },
        };
      },
    },
    reviewSentToCustomer: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(orderUUID: string) {
        return { payload: { order: { uuid: orderUUID } } };
      },
    },
    saveOrderTags: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(orderUUID: string, tags: string[]) {
        return { payload: { order: { uuid: orderUUID }, tags } };
      },
    },
    sentShippedToCustomer: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(orderUUID: string) {
        return { payload: { order: { uuid: orderUUID } } };
      },
    },
    orderSetAttachmentPublic: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(orderUUID: string, attachmentUUID: string, isPublic: boolean) {
        return {
          payload: {
            order: { uuid: orderUUID },
            attachment: { uuid: attachmentUUID, isPublic },
          },
        };
      },
    },
    orderSetAttachmentPublicResponse: {
      reducer(state) {
        state.inProcess = false;
      },
      prepare(attachmentUUID: string, isPublic: boolean) {
        return {
          payload: { attachment: { uuid: attachmentUUID, isPublic } },
        };
      },
    },
    orderLoadCustomer: {
      reducer(state) {
        state.customer = undefined;
      },
      prepare(orderUUID: string) {
        return { payload: { order: { uuid: orderUUID } } };
      },
    },
    orderLoadCustomerResponse: {
      reducer(state, action: { payload: { customer: IOrderCustomer } }) {
        state.customer = action.payload.customer;
      },
      prepare(customer: IOrderCustomer) {
        return { payload: { customer } };
      },
    },
    orderFakturoidSyncContact: {
      reducer(state) {
        state.fakturoidSyncInProcess = true;
        state.fakturoidSyncResult = null;
      },
      prepare(orderUUID: string) {
        return { payload: { order: { uuid: orderUUID } } };
      },
    },
    orderFakturoidSyncContactResponse: {
      reducer(
        state,
        action: {
          payload: {
            customer: IOrderCustomer;
            result: IFakturoidSyncContactResult;
          };
        },
      ) {
        state.fakturoidSyncInProcess = false;
        state.customer = action.payload.customer;
        state.fakturoidSyncResult = action.payload.result;
      },
      prepare(customer: IOrderCustomer, result: IFakturoidSyncContactResult) {
        return { payload: { customer, result } };
      },
    },
    orderVerifyVatId: {
      reducer(state) {
        state.vatIdVerificationInProcess = true;
        state.vatIdVerification = null;
      },
      prepare(orderUUID: string) {
        return { payload: { order: { uuid: orderUUID } } };
      },
    },
    orderVerifyVatIdResponse: {
      reducer(
        state,
        action: { payload: { result: IVatIdVerificationResult } },
      ) {
        state.vatIdVerificationInProcess = false;
        state.vatIdVerification = action.payload.result;
      },
      prepare(result: IVatIdVerificationResult) {
        return { payload: { result } };
      },
    },
    orderCreateInvoice: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(orderUUID: string) {
        return { payload: { order: { uuid: orderUUID } } };
      },
    },
    sentShippingTimeToCustomer: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(orderUUID: string, shippingTime: string) {
        return { payload: { order: { uuid: orderUUID }, shippingTime } };
      },
    },
    orderUpdateAddress: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(orderUUID: string, addressType: EAddressType, address: IAddress) {
        return {
          payload: { order: { uuid: orderUUID }, addressType, address },
        };
      },
    },
    warehousePlaceOrder: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(orderUUID: string) {
        return { payload: { order: { uuid: orderUUID } } };
      },
    },
    warehousePlaceOrderResponse: {
      reducer(state) {
        state.inProcess = false;
      },
      prepare() {
        return { payload: {} };
      },
    },
    orderHistoryLoad: {
      reducer(state, action: { payload: { order: { uuid: string } } }) {
        state.history.inProcess = true;

        if (state.history.orderUUID !== action.payload.order.uuid) {
          state.history.orderUUID = action.payload.order.uuid;
          state.history.entries = [];
          state.history.loadedAt = 0;
        }
      },
      prepare(orderUUID: string) {
        return { payload: { order: { uuid: orderUUID } } };
      },
    },
    orderHistoryLoadResponse: {
      reducer(
        state,
        action: {
          payload: { orderUUID: string; entries: TOrderHistoryEntry[] };
        },
      ) {
        state.history.inProcess = false;
        state.history.noteInProcess = false;

        if (state.history.orderUUID !== action.payload.orderUUID) {
          return;
        }

        state.history.entries = action.payload.entries;
        state.history.loadedAt = Date.now();
      },
      prepare(orderUUID: string, entries: TOrderHistoryEntry[]) {
        return { payload: { orderUUID, entries } };
      },
    },
    orderNoteAdd: {
      reducer(state) {
        state.history.noteInProcess = true;
      },
      prepare(orderUUID: string, text: string) {
        return { payload: { order: { uuid: orderUUID }, text } };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(requestFailedMatcher('Order'), (state) => {
      resetLoadingOnRequestFailed(state);
      state.history.inProcess = false;
      state.history.noteInProcess = false;
    });
  },
});

export const {
  orderLoad,
  orderLoadResponse,
  orderSetOrderStatusCode,
  reviewSetStatusCode,
  reviewSentToCustomer,
  saveOrderTags,
  sentShippedToCustomer,
  orderSetAttachmentPublic,
  orderSetAttachmentPublicResponse,
  orderLoadCustomer,
  orderLoadCustomerResponse,
  orderFakturoidSyncContact,
  orderFakturoidSyncContactResponse,
  orderVerifyVatId,
  orderVerifyVatIdResponse,
  orderCreateInvoice,
  orderUpdateAddress,
  sentShippingTimeToCustomer,
  warehousePlaceOrder,
  warehousePlaceOrderResponse,
  orderHistoryLoad,
  orderHistoryLoadResponse,
  orderNoteAdd,
} = orderSlice.actions;
export default orderSlice.reducer;
